// ============================================================================
// CLEO'S SECRET — ProgramDashboard
// File: src/pages/ProgramDashboard.jsx
//
// Phase 4 patient-facing /program route. Three render states:
//   • State A — no plan: enrolment CTA
//   • State C — enrolled or paused: three-zone dashboard
//   • State D — completed: graduation hero + trajectory
//
// Brand pattern (locked post-SAND swap):
//   • Outer wrapper P.SAND
//   • SAND header strip with Ankh 96 + Eyebrow + page title + accent rule
//   • PAP cards inside SAND main area
//
// Single fetch on mount to /api/get-program-state; loading / error / no-email
// branches match the convention in MyResults / CleoScorePage / ClientPortal.
//
// Timeline week clicks navigate to 'weekly-module' (Session 3 route — handler
// wired now, route case lands in Session 3).
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  P, T, FF,
  Eyebrow, BtnPrimary, BtnGhost, Spinner, ErrorState,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';
import TwelveWeekTimeline from '../components/program/TwelveWeekTimeline';
import ServiceCard from '../components/program/ServiceCard';

// ─── Helpers ────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

var PHASE_LABELS = {
  foundations: 'Foundations',
  recalibration: 'Recalibration',
  optimisation: 'Optimisation',
};

function phasePill(phase) {
  if (phase === 'foundations')   return { bg: 'rgba(201, 169, 97, 0.10)', fg: P.BR2, border: 'rgba(201, 169, 97, 0.35)' };
  if (phase === 'recalibration') return { bg: 'rgba(154, 116, 48, 0.12)', fg: P.BR2, border: 'rgba(154, 116, 48, 0.40)' };
  if (phase === 'optimisation')  return { bg: 'rgba(90, 63, 24, 0.10)',   fg: P.BR2, border: 'rgba(90, 63, 24, 0.40)' };
  return { bg: P.SAND, fg: P.TF, border: P.SAND3 };
}


// ─── SectionHeading — matches MyResults / CleoScorePage scaffold ──────
function SectionHeading({ eyebrow, count }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 12,
      marginBottom: 'clamp(18px, 2vw, 24px)',
      paddingBottom: 'clamp(12px, 1.4vw, 16px)',
      borderBottom: '1px solid rgba(154,116,48,0.22)',
    }}>
      <h2 style={{
        fontFamily: FF,
        fontSize: 'clamp(13px, 1.15vw, 16px)',
        fontWeight: 700,
        letterSpacing: '0.32em',
        textTransform: 'uppercase',
        color: P.BR2,
        margin: 0,
      }}>{eyebrow}</h2>
      {count != null && (
        <span style={{
          fontFamily: FF,
          fontSize: 'clamp(12px, 1vw, 14px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          color: P.TL,
        }}>{String(count).padStart(2, '0')}</span>
      )}
    </div>
  );
}


// ─── ProgramHeader — SAND strip with Ankh 96 + Eyebrow + title + accent ───
function ProgramHeader() {
  return (
    <header style={{
      background: P.SAND,
      padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 3vw, 40px) clamp(28px, 3.5vw, 48px)',
      textAlign: 'center',
      borderBottom: '1px solid ' + P.SAND3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <div style={{ marginBottom: 'clamp(20px, 2.2vw, 28px)', display: 'flex', justifyContent: 'center' }}>
        <Ankh responsive="opener" color={P.GO} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <Eyebrow center>Your program</Eyebrow>
      </div>
      <h1 style={{
        fontFamily: FF,
        fontSize: 'clamp(28px, 3.4vw, 44px)',
        fontWeight: 400,
        lineHeight: 1.12,
        letterSpacing: '-0.012em',
        color: P.INK,
        margin: 0,
        maxWidth: 720,
      }}>
        The <span style={{ fontStyle: 'italic', color: P.BR2 }}>twelve-week</span> journey.
      </h1>
    </header>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// ProgramDashboard
// ═══════════════════════════════════════════════════════════════════════
export default function ProgramDashboard(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  // Auth-wait loop (matches CleoScorePage / AdminPanel convention)
  var emailWaitState = useState(0);
  var emailWaitMs = emailWaitState[0];
  var setEmailWaitMs = emailWaitState[1];

  useEffect(function() {
    if (email) return;
    if (emailWaitMs >= 3000) { setLoading(false); return; }
    var t = setTimeout(function() { setEmailWaitMs(emailWaitMs + 500); }, 500);
    return function() { clearTimeout(t); };
  }, [email, emailWaitMs]);

  useEffect(function() {
    if (!email) return;
    var cancelled = false;
    setLoading(true);
    setError(null);

    fetch('/api/get-program-state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        if (cancelled) return;
        if (resp.ok) {
          setData(resp.body);
          setLoading(false);
        } else {
          setError((resp.body && resp.body.error) || 'Could not load your program');
          setLoading(false);
        }
      })
      .catch(function() {
        if (cancelled) return;
        setError('Network error');
        setLoading(false);
      });

    return function() { cancelled = true; };
  }, [email]);

  // ── Loading state ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        fontFamily: FF,
        background: P.SAND,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  // ── No email (auth wait timed out) ──────────────────────────────────
  if (!email) {
    return (
      <ErrorState
        premise="Sign in to see your program."
        title="Your portal awaits."
        action={
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('login', { returnTo: 'program' }); }}>
            Sign in
          </BtnPrimary>
        }
      />
    );
  }

  // ── Error ───────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <ErrorState
        premise="Something is missing."
        title={error || 'Could not load your program.'}
        action={
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('portal'); }}>
            Return to portal
          </BtnPrimary>
        }
      />
    );
  }

  var plan = data.plan;
  var modules = data.modules || [];
  var currentWeek = data.currentWeek;
  var completed = data.completed;
  var thisWeekServices = data.thisWeekServices || [];
  var score = data.score;
  var baselineScore = data.baselineScore;
  var hasBaselineDistinct = baselineScore && score && baselineScore.id !== score.id;

  // Current module reference (Zone 1 hero)
  var currentModule = modules.find(function(m) { return m.weekNumber === currentWeek; }) || null;

  function handleWeekClick(weekNumber) {
    if (onNavigate) onNavigate('weekly-module', { week: weekNumber });
  }

  // ═══════════════════════════════════════════════════════════════════
  // STATE A — no plan enrolled
  // ═══════════════════════════════════════════════════════════════════
  if (!plan) {
    return (
      <div style={{ fontFamily: FF, background: P.SAND, minHeight: '100vh' }}>
        <ProgramHeader />
        <main style={{
          maxWidth: 920,
          margin: '0 auto',
          padding: 'clamp(40px, 5vw, 80px) clamp(24px, 4vw, 64px) clamp(64px, 8vw, 120px)',
        }}>
          <div style={{
            background: P.PAP,
            border: '1px solid ' + P.SAND3,
            padding: 'clamp(36px, 4.4vw, 64px) clamp(28px, 3.6vw, 56px)',
            textAlign: 'center',
          }}>
            <p style={{
              ...T.premise,
              color: P.BR2,
              marginBottom: 'clamp(20px, 2.4vw, 28px)',
            }}>Your transformation begins here.</p>
            <h2 style={{
              fontFamily: FF,
              fontSize: 'clamp(28px, 3.4vw, 44px)',
              fontWeight: 400,
              lineHeight: 1.16,
              letterSpacing: '-0.012em',
              color: P.INK,
              margin: '0 0 clamp(28px, 3vw, 40px)',
            }}>
              The twelve-week journey.
            </h2>
            <div style={{
              width: 'clamp(180px, 22vw, 280px)',
              height: 2,
              background: P.GO,
              opacity: 0.9,
              margin: '0 auto clamp(28px, 3.2vw, 40px)',
            }} />
            <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'left' }}>
              <p style={{ ...T.bodyLg, color: P.TX, lineHeight: 1.85, marginBottom: 'clamp(18px, 2vw, 24px)' }}>
                The twelve-week program is the structured therapeutic relationship that grows from your diagnostic experience. Three phases: Foundations, Recalibration, Optimisation. Four weeks each, tuned to what your pathology and intake conversation revealed.
              </p>
              <p style={{ ...T.bodyLg, color: P.TX, lineHeight: 1.85, marginBottom: 'clamp(18px, 2vw, 24px)' }}>
                Each week brings a lead video, the science behind your protocol, and the practical steps to follow. Week 7 is a mid-program review with your GP. Week 12 closes with a full pathology retest and a graduation conversation.
              </p>
              <p style={{ ...T.bodyLg, color: P.TX, lineHeight: 1.85, marginBottom: 'clamp(32px, 3.6vw, 48px)' }}>
                Care team messaging and ongoing membership begin when you finish.
              </p>
            </div>
            <BtnPrimary onClick={function() { if (onNavigate) onNavigate('plan-preview'); }} large>
              Begin your program
            </BtnPrimary>
          </div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // STATE D — graduated
  // ═══════════════════════════════════════════════════════════════════
  if (completed) {
    var scoreDelta = (score && baselineScore && baselineScore.id !== score.id)
      ? score.overallScore - baselineScore.overallScore
      : null;
    return (
      <div style={{ fontFamily: FF, background: P.SAND, minHeight: '100vh' }}>
        <ProgramHeader />
        <main style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: 'clamp(40px, 5vw, 80px) clamp(20px, 3.5vw, 48px) clamp(64px, 8vw, 120px)',
        }}>
          {/* Graduation hero */}
          <section style={{
            background: P.OBS,
            color: P.IVY,
            padding: 'clamp(40px, 5vw, 72px) clamp(28px, 3.6vw, 56px)',
            textAlign: 'center',
            marginBottom: 'clamp(40px, 4.5vw, 56px)',
          }}>
            <p style={{
              ...T.premise,
              color: P.GO2,
              marginBottom: 'clamp(20px, 2.4vw, 28px)',
            }}>You completed the twelve weeks.</p>
            <h2 style={{
              fontFamily: FF,
              fontSize: 'clamp(28px, 3.4vw, 44px)',
              fontWeight: 400,
              lineHeight: 1.16,
              letterSpacing: '-0.012em',
              color: P.IVY,
              margin: '0 0 clamp(20px, 2.4vw, 28px)',
            }}>{plan.packageDisplayName || 'Your program'}.</h2>
            {plan.completedAt && (
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(13px, 1.1vw, 15px)',
                fontStyle: 'italic',
                color: 'rgba(240,232,212,0.7)',
                marginBottom: 'clamp(28px, 3vw, 40px)',
              }}>Graduated {formatDate(plan.completedAt)}</div>
            )}
            {hasBaselineDistinct && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'clamp(20px, 3vw, 40px)',
                flexWrap: 'wrap',
                marginTop: 'clamp(20px, 2.4vw, 32px)',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
                    fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                    color: 'rgba(224,198,133,0.7)', marginBottom: 8,
                  }}>Baseline</div>
                  <div style={{
                    fontFamily: FF, fontSize: 'clamp(48px, 6vw, 72px)', fontWeight: 400,
                    color: P.IVY, lineHeight: 1, letterSpacing: '-0.02em',
                  }}>{baselineScore.overallScore}</div>
                </div>
                <div style={{
                  fontFamily: FF, fontSize: 'clamp(24px, 2.4vw, 32px)',
                  color: P.GO2, fontStyle: 'italic',
                }}>&rarr;</div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
                    fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                    color: 'rgba(224,198,133,0.7)', marginBottom: 8,
                  }}>Graduation</div>
                  <div style={{
                    fontFamily: FF, fontSize: 'clamp(48px, 6vw, 72px)', fontWeight: 400,
                    color: P.GO, lineHeight: 1, letterSpacing: '-0.02em',
                  }}>{score.overallScore}</div>
                </div>
              </div>
            )}
            {scoreDelta != null && (
              <div style={{
                fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 18px)',
                fontStyle: 'italic',
                color: scoreDelta > 0 ? P.GO2 : 'rgba(224,198,133,0.7)',
                marginTop: 'clamp(20px, 2.2vw, 28px)',
              }}>
                {scoreDelta > 0 ? '+' : ''}{scoreDelta} points across the twelve weeks
              </div>
            )}
          </section>

          {/* Timeline (all completed) */}
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 56px)' }}>
            <SectionHeading eyebrow="Your twelve weeks" count={12} />
            <TwelveWeekTimeline
              modules={modules}
              currentWeek={12}
              onWeekClick={handleWeekClick}
            />
          </section>

          {/* Graduation summary stub */}
          <section style={{ textAlign: 'center', marginTop: 'clamp(40px, 5vw, 64px)' }}>
            <BtnPrimary onClick={function() {}} disabled>
              View graduation summary
            </BtnPrimary>
            <p style={{
              fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 15px)',
              fontStyle: 'italic', color: P.TF,
              marginTop: 'clamp(14px, 1.6vw, 20px)',
            }}>
              Graduation summary PDF arrives Phase 7.
            </p>
          </section>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // STATE C — enrolled or paused
  // ═══════════════════════════════════════════════════════════════════
  var phasePillStyle = currentModule ? phasePill(currentModule.phase) : null;
  var isPaused = plan.status === 'paused';

  return (
    <div style={{ fontFamily: FF, background: P.SAND, minHeight: '100vh' }}>
      <ProgramHeader />
      <main style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 3.5vw, 48px) clamp(64px, 8vw, 120px)',
      }}>

        {/* Paused banner */}
        {isPaused && (
          <div style={{
            background: P.PAP,
            border: '1px solid ' + P.GO,
            borderLeft: '4px solid ' + P.GO,
            padding: 'clamp(20px, 2.4vw, 28px) clamp(24px, 2.8vw, 36px)',
            marginBottom: 'clamp(32px, 3.6vw, 48px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 18,
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                color: P.BR2, marginBottom: 6,
              }}>Program paused</div>
              <div style={{
                fontFamily: FF, fontSize: 'clamp(16px, 1.4vw, 19px)',
                color: P.TX, lineHeight: 1.55,
              }}>
                Your program is paused.
                {plan.pausedAt ? ' Paused on ' + formatDate(plan.pausedAt) + '.' : ''}
              </div>
            </div>
            <BtnGhost onClick={function() {}} disabled>Resume</BtnGhost>
          </div>
        )}

        {/* ═════ Zone 1 — Today & this week ═════ */}
        <section style={{
          marginBottom: 'clamp(40px, 4.5vw, 64px)',
          opacity: isPaused ? 0.55 : 1,
        }}>
          <SectionHeading eyebrow="This week" />

          {currentModule ? (
            <div style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              padding: 'clamp(28px, 3.2vw, 48px)',
              marginBottom: 18,
            }}>
              {/* Week badge + phase pill row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 'clamp(18px, 2vw, 24px)', flexWrap: 'wrap' }}>
                <div style={{
                  fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
                  fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                  color: P.BR2,
                }}>Week {String(currentWeek).padStart(2, '0')} of 12</div>
                {phasePillStyle && (
                  <span style={{
                    display: 'inline-block',
                    fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
                    padding: '5px 10px',
                    background: phasePillStyle.bg, color: phasePillStyle.fg,
                    border: '1px solid ' + phasePillStyle.border,
                  }}>{PHASE_LABELS[currentModule.phase] || currentModule.phase}</span>
                )}
                {currentModule.isMandatoryMilestone && (
                  <span style={{
                    display: 'inline-block',
                    fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
                    padding: '5px 10px',
                    background: P.GO, color: P.INK,
                    border: '1px solid ' + P.GO,
                  }}>{currentModule.milestoneLabel || 'Milestone'}</span>
                )}
              </div>

              {/* Title + subtitle */}
              <h3 style={{
                fontFamily: FF,
                fontSize: 'clamp(28px, 3vw, 40px)',
                fontWeight: 400,
                lineHeight: 1.16,
                letterSpacing: '-0.012em',
                color: P.INK,
                margin: '0 0 clamp(10px, 1.2vw, 16px)',
              }}>{currentModule.title}</h3>
              {currentModule.subtitle && (
                <p style={{
                  fontFamily: FF,
                  fontSize: 'clamp(18px, 1.8vw, 24px)',
                  fontStyle: 'italic',
                  color: P.BR2,
                  marginBottom: 'clamp(24px, 2.8vw, 32px)',
                  lineHeight: 1.45,
                  margin: '0 0 clamp(24px, 2.8vw, 32px)',
                }}>{currentModule.subtitle}</p>
              )}

              {/* Lead video block */}
              {currentModule.leadVideoUrl ? (
                <div style={{
                  background: P.OBS,
                  color: P.IVY,
                  padding: 'clamp(20px, 2.4vw, 28px)',
                  marginBottom: 'clamp(24px, 2.6vw, 32px)',
                  border: '1px solid ' + P.GO,
                  textAlign: 'center',
                }}>
                  <div style={{
                    fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
                    fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                    color: P.GO2, marginBottom: 12,
                  }}>Lead video</div>
                  <p style={{
                    fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 17px)',
                    fontStyle: 'italic', color: 'rgba(240,232,212,0.85)',
                    margin: 0, lineHeight: 1.6,
                  }}>
                    Watch on the weekly module page.
                  </p>
                </div>
              ) : (
                <div style={{
                  background: 'rgba(232,221,196,0.55)',
                  border: '1px dashed ' + P.SAND3,
                  padding: 'clamp(28px, 3.2vw, 40px) clamp(20px, 2.4vw, 32px)',
                  marginBottom: 'clamp(24px, 2.6vw, 32px)',
                  textAlign: 'center',
                }}>
                  <div style={{
                    fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
                    fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                    color: P.TF, marginBottom: 10,
                  }}>Lead video</div>
                  <p style={{
                    fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 17px)',
                    fontStyle: 'italic', color: P.BR2,
                    margin: 0, lineHeight: 1.6,
                  }}>
                    In development by Robyn O&apos;Sullivan, GP.
                  </p>
                </div>
              )}

              {/* Mandatory actions checklist */}
              <div style={{
                fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase',
                color: P.BR2, marginBottom: 12,
              }}>This week</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 clamp(18px, 2vw, 24px)' }}>
                {[
                  { label: 'Watch the lead video', done: !!(currentModule.progress && currentModule.progress.videoWatchedAt) },
                  { label: 'Read the science',     done: !!(currentModule.progress && currentModule.progress.scienceReadAt) },
                  { label: 'Complete your check-in', done: !!currentModule.checkin },
                ].map(function(item, i) {
                  return (
                    <li key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '10px 0',
                      fontFamily: FF,
                      fontSize: 'clamp(15px, 1.3vw, 17px)',
                      color: item.done ? P.TF : P.TX,
                      textDecoration: item.done ? 'line-through' : 'none',
                    }}>
                      <span style={{
                        width: 18, height: 18,
                        border: '1px solid ' + (item.done ? P.GREEN : P.SAND3),
                        background: item.done ? P.GREEN : 'transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {item.done && (
                          <svg width={11} height={11} viewBox="0 0 16 16" fill="none">
                            <path d="M3 8.5 L7 12 L13 4" stroke={P.PAP} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span>{item.label}</span>
                    </li>
                  );
                })}
              </ul>

              {/* Open week button (stub for Session 3) */}
              <div style={{ textAlign: 'right' }}>
                <BtnGhost onClick={function() { handleWeekClick(currentWeek); }}>
                  Open this week
                </BtnGhost>
              </div>
            </div>
          ) : (
            <div style={{
              background: 'rgba(232,221,196,0.55)',
              border: '1px dashed ' + P.SAND3,
              padding: 'clamp(40px, 5vw, 60px) 24px',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 17px)',
                fontStyle: 'italic', color: P.BR2, margin: 0,
              }}>
                Module content for this week not yet authored.
              </p>
            </div>
          )}

          {/* This week's services */}
          <div style={{ marginTop: 'clamp(24px, 2.8vw, 36px)' }}>
            <div style={{
              fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
              color: P.BR2, marginBottom: 14,
            }}>Scheduled this week</div>
            {thisWeekServices.length > 0 ? (
              thisWeekServices.map(function(s) {
                return <ServiceCard key={s.id} service={s} />;
              })
            ) : (
              <div style={{
                background: 'rgba(232,221,196,0.55)',
                border: '1px dashed ' + P.SAND3,
                padding: 'clamp(20px, 2.4vw, 28px)',
                textAlign: 'center',
              }}>
                <p style={{
                  fontFamily: FF, fontSize: 'clamp(14px, 1.2vw, 16px)',
                  fontStyle: 'italic', color: P.TF, margin: 0,
                }}>No services this week.</p>
              </div>
            )}
          </div>

          {/* Care team activity stub */}
          <div style={{ marginTop: 'clamp(20px, 2.4vw, 28px)' }}>
            <p style={{
              fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 15px)',
              fontStyle: 'italic', color: P.TF,
              margin: 0, textAlign: 'center',
            }}>
              Care team messaging arrives in Phase 6.
            </p>
          </div>
        </section>

        {/* ═════ Zone 2 — Twelve-week timeline ═════ */}
        <section style={{
          marginBottom: 'clamp(40px, 4.5vw, 64px)',
          opacity: isPaused ? 0.55 : 1,
        }}>
          <SectionHeading eyebrow="Your twelve weeks" count={12} />
          <TwelveWeekTimeline
            modules={modules}
            currentWeek={currentWeek}
            onWeekClick={handleWeekClick}
          />
        </section>

        {/* ═════ Zone 3 — Cleo Score placeholder ═════ */}
        <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
          <SectionHeading eyebrow="Your score" />
          <div style={{
            background: P.PAP,
            border: '1px solid ' + P.SAND3,
            padding: 'clamp(28px, 3.2vw, 40px)',
            textAlign: 'center',
          }}>
            {score ? (
              <React.Fragment>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'clamp(24px, 3vw, 48px)',
                  flexWrap: 'wrap',
                  marginBottom: 'clamp(24px, 2.8vw, 32px)',
                }}>
                  {hasBaselineDistinct && (
                    <React.Fragment>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 12px)',
                          fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                          color: P.TF, marginBottom: 6,
                        }}>Baseline</div>
                        <div style={{
                          fontFamily: FF, fontSize: 'clamp(40px, 5vw, 60px)',
                          fontWeight: 400, color: P.TL,
                          lineHeight: 1, letterSpacing: '-0.02em',
                        }}>{baselineScore.overallScore}</div>
                      </div>
                      <div style={{
                        fontFamily: FF, fontSize: 'clamp(20px, 2vw, 28px)',
                        color: P.BR2, fontStyle: 'italic',
                      }}>&rarr;</div>
                    </React.Fragment>
                  )}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 12px)',
                      fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                      color: P.BR2, marginBottom: 6,
                    }}>Current</div>
                    <div style={{
                      fontFamily: FF, fontSize: 'clamp(48px, 6vw, 72px)',
                      fontWeight: 400, color: P.INK,
                      lineHeight: 1, letterSpacing: '-0.02em',
                    }}>{score.overallScore}</div>
                  </div>
                </div>
                {score.pattern && (
                  <p style={{
                    fontFamily: FF, fontSize: 'clamp(17px, 1.5vw, 22px)',
                    fontStyle: 'italic', color: P.BR2,
                    marginBottom: 'clamp(20px, 2.4vw, 28px)',
                    margin: '0 0 clamp(20px, 2.4vw, 28px)',
                  }}>{score.pattern}</p>
                )}
                <BtnGhost onClick={function() { if (onNavigate) onNavigate('cleo-score'); }}>
                  View full snapshot
                </BtnGhost>
                <p style={{
                  fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 14px)',
                  fontStyle: 'italic', color: P.TF,
                  margin: 'clamp(20px, 2.2vw, 28px) 0 0',
                }}>
                  Full trajectory chart arrives Phase 5.
                </p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <p style={{
                  ...T.premise,
                  color: P.BR2,
                  marginBottom: 'clamp(18px, 2vw, 24px)',
                }}>Generate your baseline.</p>
                <p style={{
                  fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 17px)',
                  color: P.TX,
                  maxWidth: 540, margin: '0 auto clamp(24px, 2.6vw, 32px)',
                  lineHeight: 1.7,
                }}>
                  Your Cleo Score baseline anchors the trajectory you will see across the twelve weeks.
                </p>
                <BtnPrimary onClick={function() { if (onNavigate) onNavigate('cleo-score'); }}>
                  Generate Cleo Score
                </BtnPrimary>
              </React.Fragment>
            )}
          </div>
        </section>

      </main>
    </div>
  );
}
